"use client";

import { useEffect, useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import ProductCarousel from "@/components/ProductCarousel";
import { getPartnerProducts } from "@/app/actions/products";
import type { Product } from "@/types";
import { useInitialRelatedProducts } from "@/components/product-detail/RelatedProductsContext";

interface PartnerProductsProps {
  partnerId: string;
  partnerName?: string;
  partnerSlug?: string;
  excludeProductIds?: string[];
  locale: string;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
}

export default function PartnerProducts({
  partnerId,
  partnerName: initialPartnerName,
  partnerSlug,
  excludeProductIds = [],
  locale,
  layout = "horizontal",
}: PartnerProductsProps) {
  const t = useTranslations("search");
  // Server-rendered first page (product detail ISR) → real cards and links in
  // the HTML; the mount-time fetch is skipped while it is in use. Name fallback
  // mirrors the getPartnerProducts action.
  const initialProducts = useInitialRelatedProducts("partner", excludeProductIds);
  const [products, setProducts] = useState<Product[]>(initialProducts ?? []);
  const [partnerName, setPartnerName] = useState(
    initialProducts
      ? initialPartnerName || initialProducts[0]?.partner_name || "이 판매자"
      : initialPartnerName || "",
  );
  const [isLoading, setIsLoading] = useState(!initialProducts);

  const excludeIdsKey = useMemo(
    () => excludeProductIds.join(","),
    [excludeProductIds]
  );

  const hasInitial = initialProducts !== undefined;

  useEffect(() => {
    if (hasInitial) return;
    async function fetchProducts() {
      setIsLoading(true);
      try {
        const excludeIds = excludeIdsKey ? excludeIdsKey.split(",") : [];
        const data = await getPartnerProducts({
          partnerId,
          partnerName: initialPartnerName,
          excludeProductIds: excludeIds,
          limit: 6,
          locale,
        });
        setProducts(data.products);
        setPartnerName(data.partnerName);
      } catch (error) {
        console.error("Failed to fetch partner products:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (partnerId) {
      fetchProducts();
    } else {
      setIsLoading(false);
    }
  }, [partnerId, initialPartnerName, excludeIdsKey, locale, hasInitial]);

  return (
    <ProductCarousel
      products={products}
      isLoading={isLoading}
      title={t("recommendations.partnerProducts", { partner: partnerName })}
      description={t("recommendations.partnerProductsDescription")}
      layout={layout}
      headerAction={
        partnerSlug ? (
          <Link
            href={`/${locale}/partner/${partnerSlug}`}
            className="flex items-center gap-1 text-sm text-orange-500 hover:text-orange-600 transition-colors font-medium"
          >
            {t("recommendations.viewAll")}
            <ChevronRight className="w-4 h-4" />
          </Link>
        ) : undefined
      }
    />
  );
}
